import {
  Box,
  Button,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  useMediaQuery,
  CircularProgress,
} from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { Upload, Trash2, X, FileText, XCircle } from "lucide-react";
import Footer from "../components/Footer";
import Navbar from "../components/Navbar";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useHotel } from "../context/HotelContext";
import LoadingGif from "../components/LoadingGif";
import { uploadHotelDocument, deleteHotelDocument } from "../services/api";
import { compressImage } from "../utils/imageCompression";

const STEPS = [
  { key: "hotelInfo", labelKey: "StepHotelInfo" },
  { key: "licenseVerification", labelKey: "StepLicenseVerification" },
  { key: "roomInfo", labelKey: "StepRoomInfo" },
  { key: "additionalInfo", labelKey: "StepAdditionalInfo" },
  { key: "paymentMethod", labelKey: "StepPaymentMethod" },
];

const CURRENT_STEP_INDEX = 3;
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

const HotelAdd_RoomInfoAdd_2 = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { id } = useParams();
  const isMobile = useMediaQuery("(max-width:950px)");
  const { saveStep, fetchDraft, loading, fetchDocuments, hotelData } = useHotel();
  const [isSaving, setIsSaving] = useState(false);
  const [uploadedDocuments, setUploadedDocuments] = useState(null);
  const [isBookingTermsUploading, setIsBookingTermsUploading] = useState(false);
  const [bookingTermsUploaded, setBookingTermsUploaded] = useState(false);
  const [requireBookingTerms, setRequireBookingTerms] = useState("");
  const [bookingTermsFile, setBookingTermsFile] = useState(null);
  const [bookingTermsPreviewUrl, setBookingTermsPreviewUrl] = useState(null);
  const [bookingTermsError, setBookingTermsError] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const bookingTermsInputRef = useRef(null);

  useEffect(() => {
    if (id) {
      fetchDraft(id);
    }
  }, [id, fetchDraft]);

  useEffect(() => {
    if (id) {
      fetchDocuments().then((response) => {
        const documents = response.data?.data || [];
        const bookingTermsFile = documents.find((doc) => doc.type === "bookingTermsFile");
        setUploadedDocuments({ bookingTermsFile });

        if (bookingTermsFile) {
          setBookingTermsUploaded(true);
          setBookingTermsFile({ name: bookingTermsFile.fileName || "Booking Terms.pdf" });
          const fileUrl = bookingTermsFile.fileUrl || bookingTermsFile.url || bookingTermsFile.link;
          setBookingTermsPreviewUrl(fileUrl);
        }
      }).catch((err) => console.error("Failed to fetch documents:", err));
    }
  }, [id, fetchDocuments]);

  const handleFileSelect = async (file, setFile, setError) => {
    if (!file) {
      setError(false);
      setFile(null);
      return;
    }

    const processedFile = file.type.startsWith("image/") ? await compressImage(file) : file;

    if (processedFile.size > MAX_FILE_SIZE_BYTES) {
      setFile(null);
      setError(true);
      return;
    }
    setError(false);
    setFile(processedFile);
  };

  useEffect(() => {
    if (!bookingTermsFile) {
      setBookingTermsPreviewUrl(null);
      return;
    }
    if (bookingTermsFile instanceof File) {
      const url = URL.createObjectURL(bookingTermsFile);
      setBookingTermsPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
  }, [bookingTermsFile]);

  const radioSx = {
    color: "#D1D1D1",
    "&.Mui-checked": { color: "#31BACD" },
  };

  const uploadBoxStyle = {
    display: "flex",
    alignItems: "center",
    gap: 2,
    backgroundColor: "#FFFFFF",
    border: "1.5px solid #D1D1D1",
    borderRadius: "10px",
    padding: isMobile ? "16px" : "20px 24px",
    cursor: "pointer",
    width: "100%",
    boxSizing: "border-box",
  };

  const fileCardStyle = {
    display: "flex",
    alignItems: "center",
    gap: 2,
    backgroundColor: "#FFFFFF",
    border: "1.5px solid #D1D1D1",
    borderRadius: "10px",
    padding: isMobile ? "16px" : "20px 24px",
    width: "100%",
    boxSizing: "border-box",
  };

  const isNextDisabled =
    !requireBookingTerms ||
    (requireBookingTerms === "yes" && (!bookingTermsFile || bookingTermsError));

  const pageStyle = {
    display: "flex",
    flexDirection: "column",
    minHeight: "100vh",
    backgroundColor: "#F5F5F5",
  };

  useEffect(() => {
    if (hotelData.bookingTerms) {
      if (hotelData.bookingTerms.requireBookingTerms === true) {
        setRequireBookingTerms("yes");
      } else if (hotelData.bookingTerms.requireBookingTerms === false) {
        setRequireBookingTerms("no");
      }
    }
  }, [hotelData]);

  const contentStyle = {
    flex: "1 0 auto",
    margin: "0 auto",
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    padding: isMobile ? "100px 20px 60px" : "120px 80px 80px",
    maxWidth: "1600px",
    width: "100%",
  };

  const handleFileUpload = async (file, setUploading, setUploaded, docType) => {
    if (!file || !id) return;
    try {
      setUploading(true);
      const response = await uploadHotelDocument(id, file, docType);
      if (response.data?.success || response.status === 201) {
        setUploaded(true);
      } else {
        console.error(`Upload failed for ${docType}`);
      }
    } catch (err) {
      console.error(`Failed to upload ${docType}:`, err);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!documentId || !id) return;
    try {
      await deleteHotelDocument(id, documentId);
      setBookingTermsFile(null);
      setBookingTermsPreviewUrl(null);
      setBookingTermsUploaded(false);
      setUploadedDocuments(null);
    } catch (err) {
      console.error("Failed to delete document:", err);
    }
  };

  const handleNext = async () => {
    try {
      setIsSaving(true);
      const bookingTerms = {
        requireBookingTerms: Boolean(requireBookingTerms === "yes"),
      };
      await saveStep("bookingTerms", bookingTerms);
      navigate(`/hotel/add/payment-1/${id}`);
    } catch (err) {
      console.error("Failed to save step:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveAndLeave = async () => {
    try {
      setIsSaving(true);
      const bookingTerms = {
        requireBookingTerms: Boolean(requireBookingTerms === "yes"),
      };
      await saveStep("bookingTerms", bookingTerms);
      navigate(`/hotel/onboarding/`);
    } catch (err) {
      console.error("Failed to save step:", err);
    } finally {
      setIsSaving(false);
    }
  };

  const isPdf = (file) => file && (file.type === "application/pdf" || file.name?.toLowerCase().endsWith(".pdf"));

  return (
    <div style={pageStyle}>
      <Navbar />
      <div style={contentStyle}>
        {/* Stepper */}
        <Box sx={{ display: "flex", alignItems: "center", mb: 6, mx: "auto" }}>
          {STEPS.map((step, index) => (
            <Box key={step.key} sx={{ display: "flex", alignItems: "center", flex: 1 }}>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}>
                <Box
                  sx={{
                    width: isMobile ? "20px" : "30px",
                    height: isMobile ? "20px" : "30px",
                    borderRadius: "50%",
                    backgroundColor: index <= CURRENT_STEP_INDEX ? "#31BACD" : "transparent",
                    border: index <= CURRENT_STEP_INDEX ? "3px solid #fff" : "2px solid #D1D1D1",
                    boxShadow: index <= CURRENT_STEP_INDEX ? "0 0 0 2px #31BACD" : "none",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                />
                <Typography
                  sx={{
                    fontSize: isMobile ? "10px" : "13px",
                    fontWeight: index === CURRENT_STEP_INDEX ? "600" : "400",
                    color: index === CURRENT_STEP_INDEX ? "#000" : "#666",
                    textAlign: "center",
                    whiteSpace: "wrap",
                    width: isMobile ? "50px" : "100px",
                  }}
                >
                  {t(step.labelKey)}
                </Typography>
              </Box>
              {index < STEPS.length - 1 && (
                <Box
                  sx={{
                    height: "1px",
                    width: isMobile ? "30px" : "200px",
                    backgroundColor: "#D1D1D1",
                    mx: "auto",
                    mb: isMobile ? "18px" : "24px",
                  }}
                />
              )}
            </Box>
          ))}
        </Box>

        <Box sx={{ width: isMobile ? "100%" : "60%", mx: "auto", display: "flex", flexDirection: "column" }}>
          {/* Heading */}
          <Typography sx={{ fontWeight: "bold", fontSize: isMobile ? "20px" : "24px", color: "#000", mb: 4 }}>
            {t('RoomInfoAddStep2Title')}
          </Typography>

          {loading ? (
            <LoadingGif />
          ) : (
            <>
              <Box sx={{ mb: bookingTermsFile || bookingTermsError ? 4 : 0 }}>
                <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 1.5 }}>
                  {t('RequireBookingTermsLabel')}
                </Typography>
                <RadioGroup
                  row
                  value={requireBookingTerms}
                  onChange={(e) => {
                    setRequireBookingTerms(e.target.value);
                    if (e.target.value === "no") {
                      setBookingTermsFile(null);
                      setBookingTermsError(false);
                    }
                  }}
                >
                  <FormControlLabel value="yes" control={<Radio sx={radioSx} />} label={t('Yes')} />
                  <FormControlLabel value="no" control={<Radio sx={radioSx} />} label={t('No')} />
                </RadioGroup>
              </Box>

              {requireBookingTerms === "yes" && (
                <Box>
                  <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000", mb: 0.5 }}>
                    {t('UploadBookingTermsLabel')}
                  </Typography>
                  <Typography sx={{ fontSize: isMobile ? "12px" : "14px", color: "#666", mb: 2 }}>
                    {t('SupportedFormatsPdf')}
                  </Typography>

                  {!bookingTermsFile ? (
                    <Box
                      component="label"
                      sx={uploadBoxStyle}
                    >
                      <Box
                        sx={{
                          width: isMobile ? "36px" : "44px",
                          height: isMobile ? "36px" : "44px",
                          borderRadius: "50%",
                          border: "1.5px solid #D1D1D1",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <Upload size={18} color="#666" />
                      </Box>
                      <Box>
                        <Typography sx={{ fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                          {t('BookingTermsLabel')}
                        </Typography>
                      </Box>
                      <input
                        ref={bookingTermsInputRef}
                        type="file"
                        accept="application/pdf"
                        style={{ display: "none" }}
                        onChange={(e) =>
                          handleFileSelect(e.target.files?.[0] || null, setBookingTermsFile, setBookingTermsError)
                        }
                      />
                    </Box>
                  ) : null}
                  {bookingTermsError && (
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        backgroundColor: "#FFFFFF",
                        border: "1.5px solid #F44336",
                        borderRadius: "10px",
                        padding: isMobile ? "16px" : "20px 24px",
                        width: "100%",
                        boxSizing: "border-box",
                      }}
                    >
                      <XCircle size={20} color="#F44336" />
                      <Typography sx={{ fontSize: isMobile ? "13px" : "14px", color: "#F44336" }}>
                        {t('FileTooLargeError')}
                      </Typography>
                    </Box>
                  )}
                  {bookingTermsFile && (
                    <Box
                      sx={fileCardStyle}
                      onClick={() => {
                        setPreviewFile({ file: bookingTermsFile, url: bookingTermsPreviewUrl });
                      }}
                    >
                      <Box
                        sx={{
                          width: isMobile ? "40px" : "48px",
                          height: isMobile ? "40px" : "48px",
                          borderRadius: "6px",
                          overflow: "hidden",
                          flexShrink: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "#F5F5F5",
                          border: "1px solid #E0E0E0",
                        }}
                      >
                        {isPdf(bookingTermsFile) ? (
                          <FileText size={22} color="#999" />
                        ) : (
                          <Box
                            component="img"
                            src={bookingTermsPreviewUrl}
                            alt={bookingTermsFile.name}
                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                        )}
                      </Box>
                      <Typography sx={{ flex: 1, fontWeight: "600", fontSize: isMobile ? "14px" : "16px", color: "#000" }}>
                        {t('BookingTermsLabel')}
                      </Typography>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          if (isBookingTermsUploading) return;
                          if (bookingTermsUploaded) {
                            console.log("Deleting document with ID:", uploadedDocuments);
                            handleDeleteDocument(uploadedDocuments?.bookingTermsFile?.documentId);
                          } else {
                            handleFileUpload(bookingTermsFile, setIsBookingTermsUploading, setBookingTermsUploaded, "bookingTermsFile");
                          }
                        }}
                        sx={{ minWidth: "auto", padding: "8px", color: bookingTermsUploaded ? "#666" : "#31BACD", textTransform: "none", }}
                      >
                        {isBookingTermsUploading ? (
                          <CircularProgress size={18} sx={{ color: "#31BACD" }} />
                        ) : bookingTermsUploaded ? (
                          <Trash2 size={18} />
                        ) : (
                          t('Upload')
                        )}
                      </Button>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}

          {/* Bottom actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              mt: 6,
            }}
          >
            <Button
              onClick={handleSaveAndLeave}
              disabled={isNextDisabled || isSaving}
              sx={{
                color: "#666",
                textTransform: "none",
                fontSize: isMobile ? "14px" : "16px",
                textDecoration: "underline",
                "&:disabled": { opacity: 0.5 },
              }}
            >
              {t('SaveAndLeave')}
            </Button>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="outlined"
                onClick={() => navigate("/hotel/add/room-info-add-1/" + id)}
                sx={{
                  color: "#666",
                  borderColor: "#D1D1D1",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { borderColor: "#999", backgroundColor: "transparent" },
                }}
              >
                {t('PreviousStep')}
              </Button>
              <Button
                variant="contained"
                disabled={isNextDisabled || isSaving}
                onClick={handleNext}
                sx={{
                  backgroundColor: "#31BACD",
                  color: "#fff",
                  borderRadius: "10px",
                  textTransform: "none",
                  fontSize: isMobile ? "14px" : "16px",
                  padding: "10px 32px",
                  "&:hover": { opacity: 0.85, backgroundColor: "#31BACD" },
                  "&:disabled": { opacity: 0.5, color: "#fff" },
                }}
              >
                {isSaving ? <CircularProgress size={20} color="inherit" /> : t('NextStep')}
              </Button>
            </Box>
          </Box>
        </Box>
      </div>
      <Footer />

      {previewFile && (
        <Box
          onClick={() => setPreviewFile(null)}
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: isMobile ? "20px" : "40px",
          }}
        >
          <Box
            onClick={(e) => e.stopPropagation()}
            sx={{
              backgroundColor: "#fff",
              borderRadius: "10px",
              width: "100%",
              maxWidth: "800px",
              maxHeight: "90vh",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: "12px 16px",
                borderBottom: "1px solid #E0E0E0",
              }}
            >
              <Typography sx={{ fontWeight: "600", fontSize: "14px", color: "#000" }}>
                {previewFile.file.name}
              </Typography>
              <Button
                onClick={() => setPreviewFile(null)}
                sx={{ minWidth: "auto", padding: "6px", color: "#666" }}
              >
                <X size={20} />
              </Button>
            </Box>
            <Box sx={{ flex: 1, overflow: "auto", backgroundColor: "#F5F5F5" }}>
              {isPdf(previewFile.file) ? (
                <Box
                  component="iframe"
                  src={previewFile.url}
                  onError={() => console.error("Failed to load PDF:", previewFile.url)}
                  sx={{ width: "100%", height: isMobile ? "70vh" : "80vh", border: "none" }}
                />
              ) : (
                <Box
                  component="img"
                  src={previewFile.url}
                  alt={previewFile.file.name}
                  onError={() => console.error("Failed to load image:", previewFile.url)}
                  sx={{ width: "100%", height: "auto", display: "block" }}
                />
              )}
            </Box>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default HotelAdd_RoomInfoAdd_2;
